import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import '../Dashboard.css';
import withRoleAccess from '../../hoc/withRoleAccess';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Row, 
  Col, 
  Spin, 
  message 
} from 'antd';

const EditStudentModal = ({ isOpen, onClose, onSave, studentId }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        phone: '',
        address: '',
        email: '',
        photourl: '',
        currenteducationlevel: '',
        guardiancontact: '',
        schoolname: '',
        schoolid: '',
        nickname: '',
        date_of_birth: '',
        nationalid: '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (studentId && isOpen) {
            const fetchStudent = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(`${API_URL}/students/${studentId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setFormData(response.data);
                } catch (error) {
                    console.error('Error fetching student:', error);
                    setError(t('fetchStudentError'));
                    message.error(t('fetchStudentError'));
                } finally {
                    setLoading(false);
                }
            };

            fetchStudent();
        }
    }, [studentId, isOpen, API_URL, t]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // For Ant Design Input components
    const handleInputChange = (name, value) => {
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Add any missing fields or default values
        const updatedData = {
            ...formData,
            guardianname: formData.guardianname || '',
            schoolid: formData.schoolid || '',
            national_id_pdf: formData.national_id_pdf || null
        };
        onSave(updatedData);
    };

    if (!isOpen) return null;

    return (
        <Modal
            title={t('editStudent')}
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={800}
            destroyOnClose={true}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                    <p>{t('loading')}</p>
                </div>
            ) : error ? (
                <div style={{ color: 'red', textAlign: 'center' }}>
                    <p>{error}</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('firstName')}:</label>
                                <Input 
                                    name="firstname" 
                                    value={formData.firstname} 
                                    onChange={(e) => handleInputChange('firstname', e.target.value)} 
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('lastName')}:</label>
                                <Input 
                                    name="lastname" 
                                    value={formData.lastname} 
                                    onChange={(e) => handleInputChange('lastname', e.target.value)} 
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('email')}:</label>
                                <Input 
                                    name="email" 
                                    type="email" 
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)} 
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('nickname')}:</label>
                                <Input 
                                    name="nickname" 
                                    value={formData.nickname} 
                                    onChange={(e) => handleInputChange('nickname', e.target.value)} 
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('dateOfBirth')}:</label>
                                {/* Keep using regular input for date to maintain current working behavior */}
                                <Input 
                                    name="date_of_birth" 
                                    type="date" 
                                    value={formData.date_of_birth} 
                                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)} 
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('nationalID')}:</label>
                                <Input 
                                    name="nationalid" 
                                    value={formData.nationalid} 
                                    onChange={(e) => handleInputChange('nationalid', e.target.value)} 
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('phone')}:</label>
                                <Input 
                                    name="phone" 
                                    value={formData.phone} 
                                    onChange={(e) => handleInputChange('phone', e.target.value)} 
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('address')}:</label>
                                <Input 
                                    name="address" 
                                    value={formData.address} 
                                    onChange={(e) => handleInputChange('address', e.target.value)} 
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('photoURL')}:</label>
                                <Input 
                                    name="photourl" 
                                    value={formData.photourl} 
                                    onChange={(e) => handleInputChange('photourl', e.target.value)} 
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('currentEducationLevel')}:</label>
                                <Input 
                                    name="currenteducationlevel" 
                                    value={formData.currenteducationlevel} 
                                    onChange={(e) => handleInputChange('currenteducationlevel', e.target.value)} 
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('guardianContact')}:</label>
                                <Input 
                                    name="guardiancontact" 
                                    value={formData.guardiancontact} 
                                    onChange={(e) => handleInputChange('guardiancontact', e.target.value)} 
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ant-form-item">
                                <label className="ant-form-item-label">{t('schoolName')}:</label>
                                <Input 
                                    name="schoolname" 
                                    value={formData.schoolname} 
                                    onChange={(e) => handleInputChange('schoolname', e.target.value)} 
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Hidden input for schoolid to ensure it's included */}
                    <input 
                        type="hidden" 
                        name="schoolid" 
                        value={formData.schoolid || ''} 
                    />
                    
                    <div style={{ textAlign: 'right', marginTop: '20px' }}>
                        <Button onClick={onClose} style={{ marginRight: '8px' }}>
                            {t('cancel')}
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {t('save')}
                        </Button>
                    </div>
                </form>
            )}
        </Modal>
    );
};

export default withRoleAccess(EditStudentModal, ['admin', 'superadmin']);